import { Column, ColumnKeys, Dependencies, SubTasksResponse, SubTaskItem, SubTasksMethod } from '../types';
import { GetRowsRequest, GetSummaryResponse, UpdateSettingsRequest, UpdateSettingsResponse } from '../../../grid/types/common';
import { ConsoleRequest, DataProviderType, FilterKeyMapper } from '../../../utils/request';
import { API_TYPE_QUARANTINE } from '../../../webcomponents.constants';
import { GridBaseDataProvider } from '../../../grid/GridBaseDataProvider';
import { TaskType } from '../../networkTasks/types';

export class SubTasksGridDataProvider extends GridBaseDataProvider<Column, ColumnKeys>  {
  private endpoint = 'Tasks';
  private cache: SubTasksResponse | null;
  public companyFilterDataProvider = API_TYPE_QUARANTINE;

  /**
   * Maps column key to different filter key as we filter with a different one.
   */
  private filterKeyMapper: FilterKeyMapper = {
    'companies.name': 'companies.id',
  };

  constructor(public dependencies: Dependencies) {
    super();
  }

  async getSummary(request: GetRowsRequest<Column, ColumnKeys>): Promise<GetSummaryResponse> {
    if (!this.dependencies.task?.id) {
      return {
        numItems: 0,
        summary: '',
      };
    }

    const response = await this.readSubTasks(request);
    return {
      numItems: response.total,
      summary: '',
    };
  }

  async getRows(request: GetRowsRequest<Column, ColumnKeys>): Promise<{ rawEvents: Array<SubTaskItem> }> {
    if (!this.dependencies.task?.id) {
      return {
        rawEvents: [],
      };
    }
    const response = await this.readSubTasks(request);
    return { rawEvents: response.data };
  }

  async setFilters(request: any): Promise<{} | undefined> {
    return;
  }

  async updateSettings(request: UpdateSettingsRequest<Column>): Promise<UpdateSettingsResponse> {
    return {};
  }

  private async readSubTasks(request: GetRowsRequest<Column, ColumnKeys>): Promise<SubTasksResponse> {
    if (!this.dependencies.task?.id) {
      return {
        success: true,
        data: [],
        total: 0,
      };
    }

    let response: SubTasksResponse;

    if (this.cache) {
      response = this.cache;
      this.cache = null;
      return response;
    }

    const payload = ConsoleRequest.mapGridRequestToPayload(request, this.dependencies.getTimezone(), this.filterKeyMapper);
    payload.internalId = this.dependencies.task.id;

    const dictionaryLoadPromise = this.loadSubtasksTranslations(this.dependencies.task.type);
    response = await ConsoleRequest.createRequest<SubTasksResponse>({
      dataProviderType: DataProviderType.ENDPOINTS,
      payload,
      endpoint: this.endpoint,
      method: SubTasksMethod.READ_SUBTASKS,
    });
    await dictionaryLoadPromise;

    this.cache = response;

    return response;
  }

  /**
   * Some sub-tasks have details that need dynamic translations. This method will load required dictionaries.
   *
   * @param parentTaskType
   * @private
   */
  private async loadSubtasksTranslations(parentTaskType: TaskType): Promise<void> {
    switch (parentTaskType) {
      case TaskType.TASK_TYPE_IOR_FIX_PARENT:
      case TaskType.TASK_TYPE_IOR_ROLLBACK_PARENT:
        await Application.modules.riskManagement.controller.helpers.routinesTranslations.initRoutinesTranslationsAsync();
        break;

      default:
        break;
    }
  }
}
